import { type ApolloError, TypedDocumentNode, useApolloClient } from '@apollo/client'
import {
  DataSourceDefinition,
  FiltersDefinition,
  FiltersState,
  GroupingDefinition,
  PaginatedFetchOptions,
  PaginatedResponse,
  RecordType,
  SortingsDefinition,
  SortingsStateMultiple,
  useDataSource,
} from '@factorialco/f0-react'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

// Define a meaningful named constant for the default page size
export const DEFAULT_PAGE_SIZE = 10

export type PaginationVars = {
  perPage: number
  __notExcludePaginationKeysFromKeyArgs?: boolean
} & (
  | { page?: number; cursor?: never; after?: never; __paginationKind: 'offset' }
  | {
      cursor?: string | number
      after?: string | null
      page?: never
      __paginationKind: 'cursor'
      __paginationInfiniteScroll?: boolean
    }
)

export type MapVariablesParams<Filters extends FiltersDefinition> = {
  filters: FiltersState<Filters>
  sortings?: SortingsStateMultiple
  search?: string
  pagination?: { cursor?: number | string | null; currentPage?: number; perPage?: number }
}

/** Helper function to create a GraphQL data source definition from a GraphQL data source definition and infer the types from the definition
 */
export const createGraphqlDataSourceDefinition = <
  R extends RecordType,
  Filters extends FiltersDefinition,
  Sortings extends SortingsDefinition,
  Variables extends PaginationVars,
  Document extends TypedDocumentNode<ResultOf<Document>, Variables & VariablesOf<Document>>,
  Grouping extends GroupingDefinition<R>,
>(
  def: GraphqlDataSourceDefinition<R, Filters, Sortings, Variables, Document, Grouping>
): GraphqlDataSourceDefinition<R, Filters, Sortings, Variables, Document, Grouping> => {
  return def
}

export type GraphqlDataSourceDefinition<
  R extends RecordType = RecordType,
  Filters extends FiltersDefinition = FiltersDefinition,
  Sortings extends SortingsDefinition = SortingsDefinition,
  Variables extends PaginationVars = PaginationVars,
  Document extends TypedDocumentNode<ResultOf<Document>, Variables & VariablesOf<Document>> =
    TypedDocumentNode<ResultOf<unknown>, Variables & VariablesOf<unknown>>,
  Grouping extends GroupingDefinition<R> = GroupingDefinition<R>,
> = Omit<DataSourceDefinition<R, Filters, Sortings, Grouping>, 'dataAdapter'> & {
  document: Document
  fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only'
  paginationType?: 'pages' | 'infinite-scroll'
  perPage?: number
  mapVariables: ({
    filters,
    sortings,
    search,
    pagination,
  }: MapVariablesParams<Filters>) => Omit<VariablesOf<Document>, 'page' | 'perPage' | 'cursor'>
  mapData: (data: ResultOf<Document>) => Array<R>
  mapPagination: (data: ResultOf<Document>) => {
    total: number
    cursor?: string
    hasNextPage?: boolean
  }
}

export const useGraphqlDataSource = <
  Record extends RecordType,
  Filters extends FiltersDefinition,
  Sortings extends SortingsDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>,
  Grouping extends GroupingDefinition<Record>,
>(
  {
    mapVariables,
    mapData,
    mapPagination,
    document,
    fetchPolicy = 'cache-and-network',
    paginationType = 'pages',
    perPage = DEFAULT_PAGE_SIZE,
    ...rest
  }: GraphqlDataSourceDefinition<
    Record,
    Filters,
    Sortings,
    Extract<VariablesOf<Document>, PaginationVars>,
    Document,
    Grouping
  >,
  deps: ReadonlyArray<unknown> = []
): DataSourceDefinition<Record, Filters, Sortings, Grouping> => {
  const client = useApolloClient()

  const source = useDataSource<Record, Filters, Sortings, Grouping>(
    {
      ...rest,
      dataAdapter: {
        paginationType,
        perPage,
        fetchData: (options: PaginatedFetchOptions<Filters>) => {
          const { filters, sortings, search } = options
          const pagination =
            'pagination' in options
              ? options.pagination
              : { perPage: DEFAULT_PAGE_SIZE, currentPage: 1 }

          const paginationVars: PaginationVars = {
            perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
            __notExcludePaginationKeysFromKeyArgs: true,
            ...(paginationType === 'pages'
              ? { page: pagination?.currentPage || 1, __paginationKind: 'offset' as const }
              : {
                  ...(pagination?.cursor && pagination.cursor !== '0'
                    ? {
                        cursor: pagination.cursor,
                        after: pagination.cursor,
                        __paginationKind: 'cursor' as const,
                        __paginationInfiniteScroll: true,
                      }
                    : { __paginationKind: 'cursor' as const, __paginationInfiniteScroll: true }),
                }),
          }

          let finalFetchPolicy = fetchPolicy
          if (
            paginationType === 'infinite-scroll' &&
            pagination.cursor &&
            pagination.cursor !== '0'
          ) {
            finalFetchPolicy = 'network-only'
          }

          const observable = client
            .watchQuery({
              query: document,
              fetchPolicy: finalFetchPolicy,
              variables: {
                ...mapVariables({ filters, sortings, search }),
                ...paginationVars,
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(
              ({
                data,
                loading,
                error,
              }: {
                data: ResultOf<Document>
                loading: boolean
                error?: ApolloError
              }) => {
                const mappedData = mapData(data)
                const mappedPagination = mapPagination(data)

                const baseData = {
                  records: mappedData,
                  total: mappedPagination.total,
                  perPage: pagination?.perPage ?? DEFAULT_PAGE_SIZE,
                }

                const fromCache = loading && !!data

                const paginatedData: PaginatedResponse<Record> =
                  paginationType === 'pages'
                    ? {
                        ...baseData,
                        type: 'pages',
                        currentPage: pagination?.currentPage ?? 1,
                        pagesCount: Math.ceil(mappedPagination.total / paginationVars.perPage),
                      }
                    : {
                        ...baseData,
                        records: fromCache
                          ? mappedData.slice(0, paginationVars.perPage)
                          : mappedData,
                        type: 'infinite-scroll',
                        cursor: mappedPagination.cursor ?? null,
                        hasMore: Boolean(mappedPagination.hasNextPage),
                      }

                return {
                  loading,
                  error,
                  data: {
                    ...paginatedData,
                  },
                }
              }
            )

          return observable
        },
      },
    },
    deps
  )

  return source
}
